import { Alert, Button, Card, Descriptions, Space, Typography, message } from 'antd';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { assignCustomerOwner } from '../../api/customers';
import type { CustomerDetail } from '../../api/customers';
import { ApiError } from '../../api/errors';
import { PermissionCodes } from '../../auth/permissions';
import { useAuth } from '../../auth/useAuth';

/** Tham số của thẻ người phụ trách. */
export interface CustomerOwnerCardProps {
  /** Khách hàng đang xem. */
  readonly customer: CustomerDetail;

  /** Nhận khách hàng sau khi sửa. */
  readonly onSaved: (updated: CustomerDetail) => void;
}

/**
 * Thẻ chỉ định người phụ trách khách hàng.
 *
 * **Đây không phải thông tin trang trí.** Phạm vi dữ liệu *khách hàng được phân công* dựa trên trường
 * này: người bị giới hạn theo phạm vi đó chỉ thấy những khách hàng có người phụ trách là chính họ.
 * Bỏ phân công làm khách hàng biến mất khỏi danh sách của họ, nên thao tác này có quyền riêng và được
 * xếp thành một thẻ riêng thay vì lẫn vào các ô nhập khác.
 *
 * **Hiện chỉ chọn được chính mình hoặc bỏ phân công.** Máy chủ chưa có điểm cuối liệt kê người dùng,
 * nên không dựng được ô chọn người khác — và một ô chọn rỗng thì tệ hơn hẳn hai cái nút rõ nghĩa.
 * Đây là chỗ còn thiếu đã biết, sẽ hoàn thiện cùng lúc với màn hình quản trị người dùng.
 */
export function CustomerOwnerCard({ customer, onSaved }: CustomerOwnerCardProps): ReactNode {
  const { user, hasPermission } = useAuth();

  const canAssign = hasPermission(PermissionCodes.CustomerAssignOwner);

  const [isSaving, setIsSaving] = useState(false);

  const myUserId = user?.userId ?? null;
  const assignedUserId = customer.assignedSalesUserId;
  const isAssignedToMe = assignedUserId !== null && assignedUserId === myUserId;

  async function assignAsync(nextUserId: string | null): Promise<void> {
    setIsSaving(true);

    try {
      const updated = await assignCustomerOwner(customer.id, nextUserId);

      void message.success(
        nextUserId === null ? 'Đã bỏ phân công.' : 'Đã nhận phụ trách khách hàng này.',
      );
      onSaved(updated);
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không đổi được người phụ trách.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  /** Mô tả người phụ trách hiện tại bằng câu người dùng đọc được. */
  function describeAssignment(): string {
    if (assignedUserId === null) {
      return 'Chưa phân công cho ai';
    }

    if (isAssignedToMe) {
      return `${user?.fullName ?? 'Chính bạn'} (bạn)`;
    }

    // Chưa có điểm cuối tra tên người dùng theo định danh, nên hiện định danh thay vì bỏ trống —
    // bỏ trống sẽ trông như chưa phân công, mà đó là điều ngược lại với sự thật.
    return `Người khác — định danh ${assignedUserId}`;
  }

  return (
    <Card size="small" title="Người phụ trách">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="Trường này quyết định ai thấy được khách hàng"
          description={
            'Phạm vi dữ liệu khách hàng được phân công dựa trên người phụ trách: người bị giới hạn '
            + 'theo phạm vi đó chỉ thấy khách hàng của chính họ. Bỏ phân công làm khách hàng này biến '
            + 'mất khỏi danh sách của họ.'
          }
        />

        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Người phụ trách hiện tại">
            {describeAssignment()}
          </Descriptions.Item>
        </Descriptions>

        {!canAssign && (
          <Alert
            type="warning"
            showIcon
            message="Bạn không có quyền chỉ định người phụ trách"
            description={
              'Tự phân công mình phụ trách một khách hàng là tự mở rộng quyền xem của chính mình, '
              + 'nên đây là quyền riêng.'
            }
          />
        )}

        {canAssign && (
          <>
            <Space wrap>
              <Button
                type="primary"
                loading={isSaving}
                disabled={isAssignedToMe || myUserId === null}
                onClick={() => {
                  void assignAsync(myUserId);
                }}
              >
                Tôi nhận phụ trách
              </Button>

              <Button
                loading={isSaving}
                disabled={assignedUserId === null}
                onClick={() => {
                  void assignAsync(null);
                }}
              >
                Bỏ phân công
              </Button>
            </Space>

            <Typography.Text type="secondary">
              Chưa chọn được người khác: máy chủ chưa có điểm cuối liệt kê người dùng. Phần đó sẽ
              hoàn thiện cùng lúc với màn hình quản trị người dùng.
            </Typography.Text>
          </>
        )}
      </Space>
    </Card>
  );
}
